let observer;
let isLoading = false;
let productCache = new Map();
let currentPage = 1;
let hasNext = true;
let sectionId = "main-collection-product-grid";
let loadTimeout = null;

function getElements() {
  const root = document.getElementById("ProductGridContainer");
  const target = root?.querySelector(".pagination-wrapper");
  const grid = root?.querySelector("#product-grid");
  return { root, target, grid };
}

function createLoadingHTML() {
  return `<div class="use-tailwind"><div class="flex items-center justify-center p-8 text-gray-600 text-sm"><div class="w-5 h-5 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin mr-2"></div>Loading more products...</div></div>`;
}

function shouldRenderProduct(product) {
  const condition = (product.getAttribute("data-condition") || "").toLowerCase();
  const inventory = parseInt(product.getAttribute("data-inventory") || "0");
  const isDraft = product.getAttribute("data-is-draft") === "true";
  const isReserved = product.getAttribute("data-is-reserved") === "true";
  const hasPartnersTag =
    product.getAttribute("data-partners-marketplace") === "true";
  const customerType = product.getAttribute("data-customer-type");

  if (isDraft || isReserved) return false;
  if (inventory <= 0) return false;
  if (hasPartnersTag && customerType === "B2C") return false;
  if (
    condition === "" ||
    condition.includes("phantom") ||
    condition.includes("mixed") ||
    condition.includes("salvage")
  )
    return false;

  return true;
}

function extractSectionId() {
  const gridContainer = document.getElementById("ProductGridContainer");
  if (gridContainer) {
    const sectionElement = gridContainer.closest('[id^="shopify-section-"]');
    if (sectionElement) {
      const id = sectionElement.id.replace("shopify-section-", "");
      return id;
    }
  }

  const fallbackSection = document.querySelector("[data-id]");
  if (fallbackSection) {
    const id = fallbackSection.getAttribute("data-id");
    return id;
  }

  return sectionId;
}

function processResponse(responseText, isJson = false) {
  const { grid } = getElements();
  if (!grid) return;

  let htmlContent;

  if (isJson) {
    try {
      const data = JSON.parse(responseText);
      const sectionKey = Object.keys(data)[0];
      htmlContent = data[sectionKey];

      if (!htmlContent) {
        return;
      }
    } catch (error) {
      return;
    }
  } else {
    htmlContent = responseText;
  }

  const temp = document.createElement("div");
  temp.innerHTML = htmlContent;

  const newGrid = temp.querySelector("#product-grid");
  if (!newGrid) {
    return;
  }

  const pagination = temp.querySelector(".pagination-wrapper");
  hasNext = pagination?.hasAttribute("data-has-next") || false;

  const fragment = document.createDocumentFragment();
  Array.from(newGrid.children).forEach((product) => {
    if (product.tagName === "LI" && shouldRenderProduct(product)) {
      product.querySelectorAll("img[data-src]").forEach((img) => {
        if (!img.src) {
          img.src = img.dataset.src;
          img.removeAttribute("data-src");
        }
      });
      fragment.appendChild(product);
    }
  });

  grid.appendChild(fragment);
}

async function fetchPage(url, isJsonFallback = false) {
  const headers = {
    "X-Requested-With": "XMLHttpRequest",
    Accept: isJsonFallback ? "application/json" : "text/html",
  };

  const response = await fetch(url, { headers });

  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return await response.text();
}

function isTargetInRange(target) {
  const rect = target.getBoundingClientRect();
  const windowHeight = window.innerHeight;
  return rect.top <= windowHeight + 2000;
}

function scheduleNextLoadIfInRange(target) {
  if (hasNext && isTargetInRange(target)) {
    setTimeout(() => {
      if (!isLoading && hasNext && isTargetInRange(target)) {
        loadPage();
      }
    }, 100);
  }
}

async function loadPage(pageNumber = null) {
  if (isLoading) {
    return;
  }

  const { target } = getElements();
  if (!target) {
    return;
  }

  if (pageNumber) currentPage = pageNumber;
  else currentPage++;

  console.info(`PLP: Loading page ${currentPage}`);
  const startTime = performance.now();

  if (!hasNext && !pageNumber) {
    const { grid } = getElements();
    observer?.unobserve(grid);
    target.innerHTML = "";
    return;
  }

  isLoading = true;
  target.innerHTML = createLoadingHTML();

  const params = new URLSearchParams(window.location.search);
  params.set("page", currentPage);

  const detectedSectionId = extractSectionId();
  const url = `${window.location.pathname}?${params}&section_id=${detectedSectionId}`;
  const cacheKey = url;

  if (productCache.has(cacheKey)) {
    processResponse(productCache.get(cacheKey), false);
    target.innerHTML = "";
    isLoading = false;

    scheduleNextLoadIfInRange(target);

    if (!hasNext) {
      const { grid } = getElements();
      observer?.unobserve(grid);
    }
    return;
  }

  try {
    const responseText = await fetchPage(url);

    const endTime = performance.now();
    console.info(
      `PLP: Page ${currentPage} loaded in ${(endTime - startTime).toFixed(2)}ms`,
    );

    productCache.set(cacheKey, responseText);
    processResponse(responseText, false);
    target.innerHTML = "";

    scheduleNextLoadIfInRange(target);

    if (!hasNext) {
      const { grid } = getElements();
      observer?.unobserve(grid);
    }
  } catch (error) {
    try {
      const fallbackParams = new URLSearchParams(window.location.search);
      fallbackParams.set("page", currentPage);
      const fallbackUrl = `${window.location.pathname}?${fallbackParams}&sections=${detectedSectionId}`;

      target.innerHTML = createLoadingHTML();

      const responseText = await fetchPage(fallbackUrl, true);
      productCache.set(cacheKey, responseText);
      processResponse(responseText, true);
      target.innerHTML = "";

      scheduleNextLoadIfInRange(target);

      if (!hasNext) {
        const { grid } = getElements();
        observer?.unobserve(grid);
      }
    } catch (fallbackError) {
      target.innerHTML = "";
      isLoading = false;
    }
  } finally {
    if (target && target.innerHTML.includes("Loading more products")) {
      target.innerHTML = "";
    }
    isLoading = false;
  }
}

function createScrollObserver() {
  const { target, grid } = getElements();
  if (!target || !grid) return;

  const params = new URLSearchParams(window.location.search);
  currentPage = parseInt(params.get("page") || "1");
  hasNext = true;

  target.innerHTML = "";

  if (observer) observer.disconnect();

  observer = new IntersectionObserver(
    (entries) => {
      const entry = entries[0];

      if (
        entry &&
        (entry.isIntersecting ||
          entry.intersectionRatio > 0 ||
          entry.boundingClientRect.bottom < 0) &&
        !isLoading &&
        hasNext
      ) {
        if (loadTimeout) clearTimeout(loadTimeout);
        loadTimeout = setTimeout(() => {
          if (!isLoading && hasNext) {
            loadPage();
          }
        }, 50);
      }
    },
    {
      rootMargin: "0px 0px 2000px 0px",
      threshold: [0, 0.1],
    },
  );

  observer.observe(target);
  target.innerHTML = "";
}

function preloadCriticalImages() {
  const hasHeroOrBanner = Boolean(
    document.querySelector(
      '[data-content-type="banner"], [data-content-type="hero"]',
    ),
  );
  if (hasHeroOrBanner) return;

  const images = document.querySelectorAll(
    '#product-grid img[src]:not([src=""])',
  );
  Array.from(images)
    .slice(0, 2)
    .forEach((img) => {
      const href = img.currentSrc || img.src;
      if (href && !img.complete) {
        const link = document.createElement("link");
        link.rel = "preload";
        link.as = "image";
        link.href = href;
        link.fetchPriority = "high";
        const srcset = img.getAttribute("srcset");
        const sizes = img.getAttribute("sizes");
        if (srcset) link.setAttribute("imagesrcset", srcset);
        if (sizes) link.setAttribute("imagesizes", sizes);
        document.head.appendChild(link);
      }
    });
}

function shouldInitialize() {
  return (
    window.location.href.includes("/collections") ||
    (window.location.href.includes("/search") &&
      !window.location.href.includes("/products"))
  );
}

document.addEventListener("DOMContentLoaded", () => {
  if (shouldInitialize()) {
    sectionId = extractSectionId();
    createScrollObserver();
    preloadCriticalImages();
  }
});

document.addEventListener("filters-applied", () => {
  if (shouldInitialize()) {
    productCache.clear();
    sectionId = extractSectionId();
    createScrollObserver();
  }
});

if (!customElements.get('product-grid-loader')) {
  customElements.define('product-grid-loader', class extends HTMLElement {
    connectedCallback() {
      this.btn = this.querySelector('.js-view-all-btn');
      this.btnText = this.querySelector('.js-btn-text');
      this.originalText = this.btnText?.textContent || 'Load more';
      this.logic = this.dataset.logic;
      this.sectionId = this.dataset.sectionId;

      // Reliably find the nearest product grid container
      this.grid = this.closest('.block')?.querySelector('.feat-product-grid');
      
      if (!this.grid || !this.btn) return;

      this.btn.addEventListener('click', (e) => {
        e.preventDefault();
        this.loadNextPage();
      });

      if (this.logic === 'endless_scroll') {
        this.btn.style.display = 'none';
        this.initObserver();
      }

      this.resizeHandler = () => {
        this.checkButtonVisibility();
        this.prefetchIfNeeded();
      };
      window.addEventListener('resize', this.resizeHandler);
      setTimeout(() => {
        this.checkButtonVisibility();
        this.prefetchIfNeeded();
      }, 0);
    }

    disconnectedCallback() {
      if (this.resizeHandler) window.removeEventListener('resize', this.resizeHandler);
      if (this.observer) this.observer.disconnect();
    }

    checkButtonVisibility() {
      if (this.isLoading) return;
      const allItems = Array.from(this.grid.querySelectorAll('.product-grid__item'));
      const hasHidden = allItems.some(el => el.offsetParent === null);
      if (!hasHidden && !this.dataset.nextUrl) {
        this.style.display = 'none';
      } else if (this.logic !== 'endless_scroll' || !this.observer) {
        this.style.display = '';
      }
    }

    getItemsToReveal() {
      const width = window.innerWidth;
      if (width < 393) return 8;
      if (width < 1024) return 6;
      if (width < 1536) return 8;
      return 10;
    }

    async fetchNextUrl() {
      if (this.fetchPromise) return this.fetchPromise;
      if (!this.dataset.nextUrl) return;

      this.fetchPromise = (async () => {
        const url = new URL(this.dataset.nextUrl, window.location.origin);
        url.searchParams.set('section_id', this.sectionId);
        const response = await fetch(url);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        
        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        
        const newItems = Array.from(doc.querySelectorAll('.feat-product-grid .product-grid__item'));
        
        const fragment = document.createDocumentFragment();
        newItems.forEach(item => {
          item.classList.add('js-hidden-product');
          item.style.display = 'none';
          fragment.appendChild(item);
        });
        this.grid.appendChild(fragment);
        
        const newLoader = doc.querySelector('product-grid-loader');
        if (newLoader && newLoader.dataset.nextUrl) {
          this.dataset.nextUrl = newLoader.dataset.nextUrl;
          if (this.logic !== 'endless_scroll') {
            this.btn.setAttribute('href', this.dataset.nextUrl);
          }
        } else {
          this.dataset.nextUrl = '';
        }
      })();

      try {
        await this.fetchPromise;
      } finally {
        this.fetchPromise = null;
      }
    }

    async prefetchIfNeeded(depth = 0) {
      if (!this.dataset.nextUrl || depth >= 5) return; // depth check to prevent infinite background runaway fetching

      let itemsToReveal = this.getItemsToReveal();

      const allItems = Array.from(this.grid.querySelectorAll('.product-grid__item'));
      const hiddenItemsCount = allItems.filter(el => el.offsetParent === null).length;

      if (hiddenItemsCount < itemsToReveal) {
        try {
          await this.fetchNextUrl();
          this.checkButtonVisibility();
          this.prefetchIfNeeded(depth + 1); // Check again in case filtering omitted too many items
        } catch (error) {
          console.error('Error prefetching more products:', error);
        }
      }
    }

    async loadNextPage() {
      if (this.isLoading) return;
      this.isLoading = true;

      let itemsToReveal = this.getItemsToReveal();

      if (this.btnText) this.btnText.textContent = 'Loading...';
      this.btn.style.pointerEvents = 'none';

      try {
        let itemsNeeded = itemsToReveal;
        let fetchCount = 0; // Safety boundary against infinite looping empty pages

        let hiddenItems = Array.from(this.grid.querySelectorAll('.product-grid__item')).filter(el => el.offsetParent === null);

        while (itemsNeeded > 0 && fetchCount < 5) {
          if (hiddenItems.length > 0) {
            const toReveal = hiddenItems.slice(0, itemsNeeded);
            toReveal.forEach(el => {
              el.classList.remove('product-grid__item--hide-until-sm', 'product-grid__item--hide-until-lg', 'product-grid__item--hide-until-2xl', 'js-hidden-product');
              el.style.display = '';
            });
            itemsNeeded -= toReveal.length;
            hiddenItems = hiddenItems.slice(toReveal.length);
          }

          if (itemsNeeded === 0) break;
          if (!this.dataset.nextUrl) break;

          fetchCount++;
          await this.fetchNextUrl();
          hiddenItems = Array.from(this.grid.querySelectorAll('.js-hidden-product'));
        }

        const hasFinalHidden = Array.from(this.grid.querySelectorAll('.product-grid__item')).some(el => el.offsetParent === null);
        if (!hasFinalHidden && !this.dataset.nextUrl) {
          this.style.display = 'none';
        }
      } catch (error) {
        console.error('Error loading more products:', error);
      } finally {
        this.isLoading = false;
        this.btn.style.pointerEvents = 'auto';
        if (this.btnText) this.btnText.textContent = this.originalText;
        
        this.prefetchIfNeeded();
      }
    }

    initObserver() {
      this.observer = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && !this.isLoading) {
          this.loadNextPage();
        }
      }, { rootMargin: '400px' });
      
      this.observer.observe(this);
    }
  });
}
